import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { ROUTES } from '../config/constants';
import { forgotPassword } from '../services/authService';
import { trackEvent } from '../services/analytics';
import PublicPageFooter from '../components/PublicPageFooter';

interface FormData {
  email: string;
}

interface FormErrors {
  email?: string;
  recaptcha?: string;
  submit?: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    email: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const isDevMode = !process.env.REACT_APP_RECAPTCHA_SITE_KEY || process.env.NODE_ENV === 'development';
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(isDevMode ? 'dev-bypass' : null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const steps = [
    'Masukkan Email',
    'Verifikasi reCAPTCHA', 
    'Link Dikirim'
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email harus diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    
    if (!recaptchaValue && !isDevMode) {
      newErrors.recaptcha = 'Silakan verifikasi bahwa Anda bukan robot';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    if (value && errors.recaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const result = await forgotPassword({
        email: formData.email,
        recaptcha_token: recaptchaValue!
      });
      
      setSuccessMessage(result.message);
      setActiveStep(2);
      trackEvent('password_reset_requested');

    } catch (error: any) {
      if (error.response?.status === 429) {
        setErrors({ submit: 'Terlalu banyak percobaan. Silakan coba lagi dalam 30 menit.' });
      } else if (error.response?.data?.detail) {
        setErrors({ submit: error.response.data.detail });
      } else {
        setErrors({ submit: 'Terjadi kesalahan. Silakan coba lagi.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleResendRequest = () => {
    setActiveStep(0);
    setSuccessMessage('');
    setErrors({});
    setRecaptchaValue(isDevMode ? 'dev-bypass' : null);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <img
              src="/Mowilex-Corporate.png"
              alt="Mowilex Logo"
              style={{
                maxWidth: '200px',
                height: 'auto',
                marginBottom: '16px'
              }}
            />
          </Box>
          
          {/* Progress Stepper */}
          <Box sx={{ width: '100%', mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Header */}
          <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
            Reset Password
          </Typography>
          
          {/* Step Content */}
          {activeStep === 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Masukkan email Anda untuk menerima link reset password
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                {errors.submit && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.submit}
                  </Alert>
                )}
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('auth.emailAddress')}
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                />
                
                {/* reCAPTCHA - hidden in dev mode */}
                {!isDevMode && (
                  <>
                    <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                        onChange={handleRecaptchaChange}
                      />
                    </Box>
                    {errors.recaptcha && (
                      <Typography variant="body2" color="error" sx={{ mb: 2, textAlign: 'center' }}>
                        {errors.recaptcha}
                      </Typography>
                    )}
                  </>
                )}
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading || !recaptchaValue}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Kirim Link Reset Password'
                  )}
                </Button>
              </Box>
            </>
          )}
          
          {activeStep === 2 && (
            <>
              <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Email Berhasil Dikirim!
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {successMessage}
                </Typography>
              </Alert>
              
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Periksa email Anda dan klik link yang dikirimkan untuk reset password.
                  Link berlaku selama 1 jam.
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Tidak menerima email?
                </Typography>
                
                <Button
                  variant="outlined"
                  onClick={handleResendRequest}
                  sx={{ mb: 2 }}
                  disabled={isLoading}
                >
                  Kirim Ulang
                </Button>
              </Box>
            </>
          )}
          
          {/* Back to Login Link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link 
              component="button"
              variant="body2" 
              onClick={handleBackToLogin}
              sx={{ textDecoration: 'none' }}
            >
              ← Kembali ke Login
            </Link>
          </Box>
        </Paper>
      </Box>
      <PublicPageFooter />
    </Container>
  );
};

export default ForgotPasswordPage;